import { t } from '../i18n/ru';
import { Bomb, Gem } from './Icons';

// Иллюстрации к обучению. Все они декоративные (aria-hidden): смысл целиком несёт текст шага.
// Слова одни и те же на всех шагах — пример «сквозной», игрок не тратит силы на новый контекст.
type Role = 't' | 'b' | 'd';
const CELLS: { word: string; role: Role }[] = [
  { word: 'луна', role: 't' },
  { word: 'вампир', role: 'b' },
  { word: 'лиса', role: 'd' },
  { word: 'волк', role: 't' },
  { word: 'солнце', role: 'd' },
  { word: 'зуб', role: 't' },
];

const Tile = ({ word, role, cls = '' }: { word: string; role: Role; cls?: string }) => (
  <div class={`tile ${role} ${cls}`}>
    {role !== 'd' && <span class="mark">{role === 'b' ? <Bomb /> : <Gem />}</span>}
    <span class="tick tag">✓</span>
    <span class="word">{word}</span>
  </div>
);

const Meter = ({ children }: { children?: preact.ComponentChildren }) => (
  <div class="demo-meter">
    <Bomb class="demo-bomb" />
    <div class="meter">
      <i />
    </div>
    <span class="demo-meter-val">{children}</span>
  </div>
);

/** Шаг 1: слева — что видишь ты, справа — что видит ИИ. */
function DemoView() {
  return (
    <div class="demo demo-view">
      <div class="demo-col">
        <div class="demo-cap">Ты видишь</div>
        <div class="mini-grid">
          {CELLS.map((c) => (
            <Tile key={c.word} {...c} />
          ))}
        </div>
      </div>
      <div class="demo-col">
        <div class="demo-cap">ИИ видит</div>
        <div class="mini-grid">
          {CELLS.map((c) => (
            <div class="tile unk" key={c.word}>
              {c.word}
            </div>
          ))}
        </div>
      </div>
      <div class="demo-legend">
        <span>
          <i class="dot t" /> твои
        </span>
        <span>
          <i class="dot b" /> бомба
        </span>
        <span>
          <i class="dot d" /> обманки
        </span>
      </div>
    </div>
  );
}

/** Шаг 2: набираем «ночь» → ИИ думает → открывает «луна». */
function DemoClue() {
  return (
    <div class="demo demo-clue">
      <div class="mini-grid">
        {CELLS.map((c) => (
          <Tile key={c.word} {...c} cls={c.word === 'луна' ? 'pick' : 'idle'} />
        ))}
      </div>
      <div class="demo-input">
        <span class="typed typed-4">ночь</span>
        <span class="caret" />
      </div>
      <div class="demo-say">
        «ночь» → <b>луна</b> ✓
      </div>
    </div>
  );
}

/** Шаг 3: «клык» — про волка, но бомба тоже услышала: процент летит от «волка» к бомбе и на шкалу. */
function DemoHeat() {
  return (
    <div class="demo demo-heat">
      <Meter>
        <span class="v-end">12%</span>
      </Meter>
      <div class="mini-grid">
        {CELLS.map((c) => (
          <Tile key={c.word} {...c} cls={c.word === 'волк' ? 'pick' : c.role === 'b' ? 'hit' : 'idle'} />
        ))}
      </div>
      <div class="demo-input">
        <span class="typed typed-4">клык</span>
        <span class="caret" />
      </div>
      <div class="demo-say">
        «клык» → <b>волк</b> ✓ <span class="warn">бомба услышала</span>
      </div>
      <div class="demo-chip">+12%</div>
    </div>
  );
}

/** Шаг 4: две обманки подряд, затем взрыв. */
function DemoBoom() {
  return (
    <div class="demo demo-boom">
      <Meter>
        <span class="v-boom">💥</span>
      </Meter>
      <div class="mini-grid boom-grid">
        {CELLS.map((c) => (
          <Tile
            key={c.word}
            {...c}
            cls={c.word === 'лиса' ? 'miss m1' : c.word === 'солнце' ? 'miss m2' : 'idle'}
          />
        ))}
      </div>
      <div class="demo-say boom-say">
        <span class="s1">обманка: +25%</span>
        <span class="s2">ещё +25%</span>
        <span class="s3">100% — взрыв!</span>
      </div>
    </div>
  );
}

/** Шаг 5: лесенка звёзд. */
function DemoGoal() {
  const widths = ['25%', '50%', '100%'];
  return (
    <div class="demo demo-goal">
      {t.help.stars.map((row, i) => (
        <div class="goal-row" key={row.stars}>
          <span class="goal-stars">{row.stars}</span>
          <div class="goal-bar">
            <i style={{ width: widths[i] }} class={`g${i}`} />
          </div>
          <span class="goal-label">{row.label}</span>
        </div>
      ))}
    </div>
  );
}

export const DEMOS = [DemoView, DemoClue, DemoHeat, DemoBoom, DemoGoal] as const;
